/**
 * New chat API client – matches backend CHAT_NEW_SPEC.
 * Admin only (web). Base: /v1/admin/chat.
 */

import axios from 'axios';
import { API_BASE_URL } from './utils';
import { getActAsCompanyToken } from './companyAdminApi';

const ADMIN_CHAT_BASE = `${API_BASE_URL}/admin/chat`;

function getAuthToken() {
  return getActAsCompanyToken() || localStorage.getItem('company_admin_token');
}

const client = axios.create({
  baseURL: ADMIN_CHAT_BASE,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const chatApi = {
  // Groups
  getGroups: () => client.get('/groups'),
  getGroupsWithUnread: () => client.get('/groups/with-unread'),

  // Customers in group
  getCustomers: (groupId, page = 1, limit = 50, opts = {}) => {
    const params = { group_id: groupId, page, limit };
    if (opts.q) params.q = opts.q;
    if (opts.customer_id) params.customer_id = opts.customer_id;
    if (opts.lead === 'warm' || opts.lead === 'cold') params.lead = opts.lead;
    const config = opts.signal ? { signal: opts.signal } : {};
    return client.get('/customers', { params, ...config });
  },

  /** Resolve group + customer row when opening chat from profile with only ?customer= */
  getCustomerOpen: (customerId) =>
    client.get('/customer-open', { params: { customer_id: customerId } }),

  // Thread: get or create for customer (backend: POST /thread-for-customer)
  createOrGetThread: (customerId) => client.post('/thread-for-customer', { customer_id: customerId }),

  // Messages
  getMessages: (threadId, params = {}) =>
    client.get(`/threads/${threadId}/messages`, { params: { limit: 30, ...params } }),
  sendMessage: (threadId, payload) => client.post(`/threads/${threadId}/messages`, payload),
  markRead: (threadId, lastReadMessageId) =>
    client.post(`/threads/${threadId}/read`, { last_read_message_id: lastReadMessageId }),

  // Admin-only
  blockThread: (threadId, blocked) => client.post(`/threads/${threadId}/block`, { blocked }),
  deleteThread: (threadId) => client.delete(`/threads/${threadId}`),
  deleteForMe: (threadId, messageId) =>
    client.post(`/threads/${threadId}/messages/${messageId}/delete-for-me`),
  markAllRead: () => client.post('/mark-all-read'),

  deleteForAll: (threadId, messageId) =>
    client.post(`/threads/${threadId}/messages/${messageId}/delete-for-all`),

  // Single-file upload (admin & mobile). kind: 'image' | 'file' | 'audio'
  uploadMedia: (file, kind = 'file') => {
    const form = new FormData();
    form.append('file', file);
    form.append('kind', kind);
    return client.post('/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

/** Media URL for a message attachment (same host + path; token in query if needed) */
export function chatMediaUrl(mediaId) {
  const token = getAuthToken();
  const url = `${ADMIN_CHAT_BASE}/media/${mediaId}`;
  return token ? `${url}?token=${encodeURIComponent(token)}` : url;
}

export default chatApi;
